import { createContext, useContext, useEffect, useState } from 'react';
import { authAPI } from '../lib/api';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(undefined); // undefined = loading
  const [authError, setAuthError] = useState(null);

  useEffect(() => {
    // On mount, check if user is already authenticated via cookie session.
    authAPI.getMe()
      .then(res => setUser(res.data))
      .catch(() => setUser(null));
  }, []);

  const signInWithGoogle = () => {
    const clientId = process.env.REACT_APP_GOOGLE_OAUTH_CLIENT_ID;
    if (!clientId) {
      const err = new Error('Google OAuth is not configured');
      err.code = 'auth/configuration-not-found';
      throw err;
    }
    setAuthError(null);

    // Build the Google OAuth 2.0 authorization URL and redirect to it.
    // The redirect_uri MUST match exactly what is registered in Google Cloud Console.
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUri = window.location.origin + '/auth/callback';
    const params = new URLSearchParams({
      client_id: clientId,
      redirect_uri: redirectUri,
      response_type: 'code',
      scope: 'openid email profile',
      access_type: 'offline',
      prompt: 'select_account',
    });
    window.location.href = `https://accounts.google.com/o/oauth2/v2/auth?${params.toString()}`;
  };

  const logout = async () => {
    try {
      await authAPI.logout();
    } catch {
      // Ignore logout errors – the cookie will expire anyway.
    }
    setUser(null);
  };

  const value = {
    user,
    // Alias kept for components that still reference firebaseUser
    firebaseUser: user,
    idToken: null,
    isAuthLoading: user === undefined,
    authError,
    signInWithGoogle,
    logout,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>');
  return ctx;
}
